// @redux-policy: transient-display-cache
// Investor portfolio wallet/transactions are a session display cache populated
// only via portfolioThunks (API). Canonical balances live in Postgres wallets.
// Reviewed: 2026-07-28. See REDUX_VIOLATIONS.md V-P1-00W.
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchPortfolioSummary,
  fetchStressTest,
  fetchAllocations,
  fetchWalletWithTransactions,
  fetchOpportunities,
  fetchNavHistory,
} from './portfolioThunks';

const initialState = {
  summary: null,
  summaryStatus: 'idle',
  summaryError: null,

  navHistory: null,
  navHistoryStatus: 'idle',
  navHistoryError: null,

  stress: null,
  stressStatus: 'idle',
  stressError: null,

  allocations: [],
  allocationsStatus: 'idle',
  allocationsError: null,

  wallet: null,
  transactions: [],
  walletStatus: 'idle',
  walletError: null,

  opportunities: [],
  opportunitiesStatus: 'idle',
  opportunitiesError: null,

  lastFetched: null,
};

function applyAsyncReducers(builder, asyncThunk, statusKey, payloadKey, errorKey) {
  builder
    .addCase(asyncThunk.pending, (state) => {
      state[statusKey] = 'loading';
      state[errorKey] = null;
    })
    .addCase(asyncThunk.fulfilled, (state, action) => {
      state[statusKey] = 'succeeded';
      state[payloadKey] = action.payload;
      state.lastFetched = new Date().toISOString();
    })
    .addCase(asyncThunk.rejected, (state, action) => {
      state[statusKey] = 'failed';
      state[errorKey] = action.payload || action.error.message;
    });
}

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    clearPortfolio: () => initialState,
  },
  extraReducers: (builder) => {
    applyAsyncReducers(builder, fetchPortfolioSummary, 'summaryStatus', 'summary', 'summaryError');
    applyAsyncReducers(builder, fetchNavHistory, 'navHistoryStatus', 'navHistory', 'navHistoryError');
    applyAsyncReducers(builder, fetchStressTest, 'stressStatus', 'stress', 'stressError');
    applyAsyncReducers(builder, fetchAllocations, 'allocationsStatus', 'allocations', 'allocationsError');
    applyAsyncReducers(builder, fetchOpportunities, 'opportunitiesStatus', 'opportunities', 'opportunitiesError');

    // Wallet has a composite payload
    builder
      .addCase(fetchWalletWithTransactions.pending, (state) => {
        state.walletStatus = 'loading';
        state.walletError = null;
      })
      .addCase(fetchWalletWithTransactions.fulfilled, (state, action) => {
        state.walletStatus = 'succeeded';
        state.wallet = action.payload.wallet;
        state.transactions = action.payload.transactions || [];
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchWalletWithTransactions.rejected, (state, action) => {
        state.walletStatus = 'failed';
        state.walletError = action.payload || action.error.message;
      });
  },
});

export const { clearPortfolio } = portfolioSlice.actions;
export default portfolioSlice.reducer;
